/*
 * Countdowns (ENG-832, shared with ENG-834): how often one ticks and how its
 * remaining time reads ("3h 20m", "12m 05s", "under a minute"). The live
 * clock is `hooks/useCountdown.js`; these are the pure parts.
 */

export const COUNTDOWN_SECOND_MS = 1000;
export const COUNTDOWN_MINUTE_MS = 60 * COUNTDOWN_SECOND_MS;
export const COUNTDOWN_HOUR_MS = 60 * COUNTDOWN_MINUTE_MS;
export const COUNTDOWN_DAY_MS = 24 * COUNTDOWN_HOUR_MS;

/** Whole seconds left, rounded up, so the last second reads "1s" rather than "0s". */
const secondsLeft = (remainingMs) => Math.max(0, Math.ceil((Number.isFinite(remainingMs) ? remainingMs : 0) / COUNTDOWN_SECOND_MS));

/**
 * How long until a countdown with `remainingMs` left next changes what it
 * shows (countdownParts): when its minutes change while more than
 * `secondsWithinMs` is left (an hour by default; or always, without
 * `seconds`), else when its seconds change. Further out it also stops at the
 * `secondsWithinMs` mark, where seconds start.
 */
export const countdownTickMs = (remainingMs, { seconds = true, secondsWithinMs = COUNTDOWN_HOUR_MS } = {}) => {
  if (!Number.isFinite(remainingMs) || remainingMs <= 0) return COUNTDOWN_MINUTE_MS;
  const left = secondsLeft(remainingMs);
  const byMinute = remainingMs > secondsWithinMs || !seconds;
  // The whole second at which the display next changes.
  const next = byMinute ? 60 * Math.floor(left / 60) - 1 : left - 1;
  let delay = remainingMs - next * COUNTDOWN_SECOND_MS;
  if (seconds && remainingMs > secondsWithinMs) delay = Math.min(delay, remainingMs - secondsWithinMs);
  return delay > 0 ? delay : COUNTDOWN_SECOND_MS;
};

/** Hours, minutes and seconds in `remainingMs`, from its whole seconds rounded up. */
export const countdownParts = (remainingMs) => {
  const total = secondsLeft(remainingMs);
  return {
    hours: Math.floor(total / 3600),
    minutes: Math.floor((total % 3600) / 60),
    seconds: total % 60,
  };
};

/**
 * The remaining time as words (`shop.countdown.*`): "3h 20m" from an hour up;
 * under an hour "12m 05s" / "45s" when ticking by the second (`seconds`),
 * else "12m" or "under a minute". Numbers go through `format.number`.
 */
export const countdownLabel = (t, format, remainingMs, { seconds = true } = {}) => {
  const parts = countdownParts(remainingMs);
  if (parts.hours > 0) {
    return t('shop.countdown.hoursMinutes', { hours: format.number(parts.hours), minutes: format.number(parts.minutes) });
  }
  if (seconds) {
    const secs = format.number(parts.seconds, { minimumIntegerDigits: parts.minutes > 0 ? 2 : 1 });
    return parts.minutes > 0
      ? t('shop.countdown.minutesSeconds', { minutes: format.number(parts.minutes), seconds: secs })
      : t('shop.countdown.seconds', { seconds: secs });
  }
  return parts.minutes > 0
    ? t('shop.countdown.minutes', { minutes: format.number(parts.minutes) })
    : t('shop.countdown.underAMinute');
};

/**
 * The remaining time as a clock (ENG-876), for an item leaving the shop in
 * its last 24 hours: "05:12:09" (hh:mm:ss), or "05:12" without `seconds`
 * (the device asks for reduced motion, so it ticks by the minute; minutes
 * round up then, so the last minute reads "00:01", not "00:00"). Digits go
 * through `format.number`, at least two of each.
 */
export const countdownClock = (format, remainingMs, { seconds = true } = {}) => {
  const two = (n) => format.number(n, { minimumIntegerDigits: 2, useGrouping: false });
  if (!seconds) {
    const minutes = Math.ceil(secondsLeft(remainingMs) / 60);
    return `${two(Math.floor(minutes / 60))}:${two(minutes % 60)}`;
  }
  const parts = countdownParts(remainingMs);
  return `${two(parts.hours)}:${two(parts.minutes)}:${two(parts.seconds)}`;
};
